import clsx from 'clsx';
import React, {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import InfoIcon from 'components/assets/info-qmee.svg';
import QmeeCloseButton from 'components/assets/Qmee-close-button.svg';
import Overlay from 'components/atoms/Overlay/Overlay';
import Card from 'components/atoms/Card/Card';
import Txt from 'components/atoms/Txt/Txt';
import ActivityBanner from 'components/molecules/ActivityBanner/ActivityBanner';
import LoadingIndicatorSmall from 'components/atoms/LoadingIndicator/LoadingIndicatorSmall';
import { useSubmitPollMutation } from 'hooks/useSubmitPollMutation';
import type { PopQuizBannerQuery } from './__generated__/PopQuizBannerQuery.graphql';
import CardBubbles from './assets/card_bubbles.svg';
import classes from './PopQuizBanner.qmee.module.scss';

type HeadingLevel = React.ComponentProps<typeof ActivityBanner>['headingLevel'];

type QuizScreen = 'start' | 'question' | 'result';
type ResultType = 'correct' | 'wrong' | 'timeout';

export type PopQuizBannerProps = {
  title?: ReactNode;
  headingLevel?: HeadingLevel;
  pollId?: string;
  question?: string;
  description?: string;
  startDate?: string | null;
  startMessage: string;
  onInfoClick?: () => void;
  infoAriaLabel?: string;
  showInfoButton?: boolean;
  infoIcon?: ReactNode;
  bubbles?: ReactNode;
  className?: string;
  headerClassName?: string;
  headerTitleClassName?: string;
  cardClassName?: string;
  descriptionClassName?: string;
  startMessageClassName?: string;
  startMessageTextClassName?: string;
};

const popQuizPollQuery = graphql`
  query PopQuizBannerQuery($id: ID!) {
    node(id: $id) {
      ... on PollQuestion {
        id
        answerTime
        reward {
          formatted
        }
        possibleAnswers {
          type
          ... on SelectionQuestionAnswers {
            answers {
              answerKey
              label
            }
          }
          ... on MultiPunchQuestionAnswers {
            answers {
              answerKey
              label
            }
          }
        }
        responseStatistics {
          key
          ratio
          correct
          selected
        }
      }
    }
  }
`;

export default function PopQuizBanner({
  title,
  headingLevel = 3,
  pollId,
  question,
  description,
  startDate,
  startMessage,
  onInfoClick,
  infoAriaLabel = 'More information',
  showInfoButton = true,
  infoIcon,
  bubbles,
  className,
  headerClassName,
  headerTitleClassName,
  cardClassName,
  descriptionClassName,
  startMessageClassName,
  startMessageTextClassName,
}: PopQuizBannerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isLive = startDate ? new Date(startDate) < new Date() : false;
  const isInteractive = !!pollId && !!question && isLive;

  const handleBannerClick = () => {
    if (isInteractive) {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleBannerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target as HTMLElement;
    if (target.closest('button')) return;
    event.preventDefault();
    handleBannerClick();
  };

  const infoContent =
    infoIcon === undefined ? <InfoIcon aria-hidden focusable="false" /> : infoIcon;

  const shouldRenderInfoButton = showInfoButton && !!onInfoClick;

  const infoButton =
    shouldRenderInfoButton && infoContent !== null ? (
      <button
        type="button"
        aria-label={infoAriaLabel}
        className={classes.infoButton}
        onClick={onInfoClick}
      >
        {infoContent}
      </button>
    ) : null;

  const bubblesNode = bubbles ?? (
    <CardBubbles className={classes.bubbles} aria-hidden focusable="false" />
  );

  return (
    <>
      <ActivityBanner
        title={title}
        headingLevel={headingLevel}
        infoButton={infoButton}
        cardBackgroundColor="var(--palette-tertiary-main)"
        className={className}
        headerClassName={headerClassName}
        headerTitleClassName={headerTitleClassName}
        cardClassName={cardClassName}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={isInteractive ? handleBannerClick : undefined}
        onKeyDown={isInteractive ? handleBannerKeyDown : undefined}
      >
        <div className={classes.content}>
          {description && (
            <Txt
              className={clsx(classes.description, descriptionClassName)}
              component="p"
              skipTranslation
            >
              {description}
            </Txt>
          )}
          <span className={clsx(classes.startMessage, startMessageClassName)}>
            <span className={clsx(classes.startMessageText, startMessageTextClassName)}>
              {startMessage}
            </span>
          </span>
        </div>
        {bubblesNode}
      </ActivityBanner>

      {isModalOpen && pollId && question && (
        <Overlay>
          <Card
            className={classes.modalRoot}
            component="div"
            role="dialog"
            aria-modal="true"
            aria-label="Pop Quiz"
          >
            <Suspense fallback={<LoadingIndicatorSmall />}>
              <PopQuizPollContent pollId={pollId} question={question} onClose={handleCloseModal} />
            </Suspense>
          </Card>
        </Overlay>
      )}
    </>
  );
}

function PopQuizPollContent({
  pollId,
  question,
  onClose,
}: {
  pollId: string;
  question: string;
  onClose: () => void;
}) {
  const data = useLazyLoadQuery<PopQuizBannerQuery>(
    popQuizPollQuery,
    { id: pollId },
    { fetchPolicy: 'store-and-network' },
  );

  const poll = data.node;
  const responseStatistics = poll?.responseStatistics ?? null;
  const hasAnswered = responseStatistics != null;
  const answerOptions = poll?.possibleAnswers?.answers ?? [];
  const answerTime = poll?.answerTime ?? 15;
  const reward = poll?.reward ?? null;

  const [hasStarted, setHasStarted] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(answerTime);

  const isSubmittingRef = useRef(false);
  const timerStartRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [commitMutation, isMutationInFlight] = useSubmitPollMutation();

  const screen: QuizScreen = hasAnswered || timedOut ? 'result' : hasStarted ? 'question' : 'start';

  const resultType = useMemo((): ResultType | null => {
    if (hasAnswered && responseStatistics) {
      const selectedStat = responseStatistics.find((stat) => stat.selected);
      if (!selectedStat) return 'timeout';
      return selectedStat.correct ? 'correct' : 'wrong';
    }
    if (timedOut) return 'timeout';
    return null;
  }, [hasAnswered, responseStatistics, timedOut]);

  const clearTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    timerStartRef.current = null;
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerStartRef.current = Date.now();
    setTimeRemaining(answerTime);

    timerIntervalRef.current = setInterval(() => {
      if (timerStartRef.current === null) return;

      const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
      const remaining = Math.max(0, answerTime - elapsed);
      setTimeRemaining(remaining);

      if (remaining === 0) {
        clearTimer();
      }
    }, 1000);
  }, [answerTime, clearTimer]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  useEffect(() => {
    if (timeRemaining === 0 && screen === 'question' && !isSubmittingRef.current) {
      isSubmittingRef.current = true;
      setTimedOut(true);
      commitMutation({
        variables: { input: { id: pollId, answers: [] } },
        onCompleted: () => {
          isSubmittingRef.current = false;
        },
        onError: () => {
          isSubmittingRef.current = false;
        },
      });
    }
  }, [timeRemaining, screen, pollId, commitMutation]);

  const handleStartQuiz = () => {
    startTimer();
    setHasStarted(true);
  };

  const handleAnswerClick = useCallback(
    (answerKey: string) => {
      if (screen !== 'question' || isSubmittingRef.current || isMutationInFlight) {
        return;
      }

      clearTimer();
      setSelectedAnswerId(answerKey);
      isSubmittingRef.current = true;

      commitMutation({
        variables: { input: { id: pollId, answer: answerKey } },
        onCompleted: () => {
          isSubmittingRef.current = false;
        },
        onError: () => {
          setSelectedAnswerId(null);
          isSubmittingRef.current = false;
        },
      });
    },
    [screen, isMutationInFlight, pollId, commitMutation, clearTimer],
  );

  const getAnswerStatistic = (answerKey: string) =>
    responseStatistics?.find((stat) => stat.key === answerKey);

  const isAnswerSelected = (answerKey: string) => {
    const statistic = getAnswerStatistic(answerKey);
    if (statistic?.selected) return true;
    return selectedAnswerId === answerKey;
  };

  const headerMessage = useMemo((): React.ReactNode => {
    if (resultType === 'correct' && reward) {
      return (
        <>
          Congratulations, you&apos;ve earned <strong>{reward.formatted}</strong>
        </>
      );
    }
    if (resultType === 'wrong') {
      return (
        <>
          <strong>Oooh!</strong> Not quite. Better luck next time.
        </>
      );
    }
    if (resultType === 'timeout') {
      return (
        <>
          <strong>Times Up!</strong> Better luck next time.
        </>
      );
    }
    return null;
  }, [resultType, reward]);

  if (screen === 'start') {
    return (
      <div className={classes.startScreenContent}>
        <div className={classes.modalHeaderPink}>
          <CardBubbles className={classes.modalHeaderIcon} aria-hidden focusable="false" />
          <span className={classes.timerDisplay}>{answerTime}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close pop quiz"
            className={classes.modalCloseButton}
          >
            <QmeeCloseButton aria-hidden focusable="false" />
          </button>
        </div>
        <div className={classes.startBody}>
          <Txt component="h3" className={classes.startTitle}>
            Daily Pop Quiz
          </Txt>
          <Txt component="p" className={classes.startSubtitle}>
            Answer Correctly and Earn
          </Txt>
          {reward && <span className={classes.rewardDisplay}>{reward.formatted}</span>}
          <Txt component="p" className={classes.startInstruction} skipTranslation>
            Once you click start, you get <strong>{answerTime} seconds</strong> to answer correctly
          </Txt>
          <button type="button" className={classes.startButton} onClick={handleStartQuiz}>
            <Txt component="span">Start Quiz</Txt>
          </button>
        </div>
      </div>
    );
  }

  if (screen === 'question') {
    return (
      <>
        <div className={classes.modalHeaderPink}>
          <CardBubbles className={classes.modalHeaderIcon} aria-hidden focusable="false" />
          <span
            className={clsx(classes.timerDisplay, timeRemaining <= 5 && classes.timerDisplayUrgent)}
          >
            {timeRemaining}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close pop quiz"
            className={classes.modalCloseButton}
          >
            <QmeeCloseButton aria-hidden focusable="false" />
          </button>
        </div>
        <div className={classes.questionBody}>
          <Txt component="p" className={classes.modalQuestion} skipTranslation>
            {question}
          </Txt>
          <div className={classes.answers}>
            {answerOptions.map((answer, index) => (
              <button
                key={answer.answerKey}
                type="button"
                disabled={isMutationInFlight}
                onClick={() => handleAnswerClick(answer.answerKey)}
                className={clsx(
                  classes.answerButton,
                  selectedAnswerId === answer.answerKey && classes.answerButtonSelected,
                )}
              >
                <span className={classes.answerIndex}>{index + 1}</span>
                <Txt component="span" className={classes.answerLabel} skipTranslation>
                  {answer.label}
                </Txt>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  const displayTimer = resultType === 'timeout' ? 0 : timeRemaining;

  return (
    <>
      <div className={classes.modalHeaderPink}>
        <CardBubbles className={classes.modalHeaderIcon} aria-hidden focusable="false" />
        <span className={classes.timerDisplay}>{displayTimer}</span>
        {headerMessage && (
          <Txt component="p" className={classes.resultMessage} skipTranslation>
            {headerMessage}
          </Txt>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close pop quiz"
          className={classes.modalCloseButton}
        >
          <QmeeCloseButton aria-hidden focusable="false" />
        </button>
      </div>
      <div className={classes.questionBody}>
        <Txt component="p" className={classes.modalQuestion} skipTranslation>
          {question}
        </Txt>
        <div className={classes.answers}>
          {answerOptions.map((answer, index) => {
            const statistic = getAnswerStatistic(answer.answerKey);
            const selected = isAnswerSelected(answer.answerKey);
            const isCorrectAnswer = statistic?.correct ?? false;
            const percentage = statistic ? Math.round(statistic.ratio) : null;

            return (
              <div
                key={answer.answerKey}
                className={clsx(
                  classes.answerButton,
                  classes.answerButtonResult,
                  isCorrectAnswer && classes.answerButtonCorrectResult,
                  selected && !isCorrectAnswer && classes.answerButtonWrongResult,
                )}
              >
                {percentage != null && (
                  <span
                    className={clsx(
                      classes.answerBar,
                      isCorrectAnswer && classes.answerBarCorrect,
                      selected && !isCorrectAnswer && classes.answerBarWrong,
                    )}
                    style={{ inlineSize: `${percentage}%` }}
                  />
                )}
                <span className={classes.answerIndex}>{index + 1}</span>
                <Txt component="span" className={classes.answerLabel} skipTranslation>
                  {answer.label}
                </Txt>
                {percentage != null && (
                  <span className={classes.answerPercentage}>{percentage}%</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
